// src/pages/Moderation/ModerationQueuePage/ui/QueueFilters/QueueFilters.tsx
import React, { useEffect, useMemo, useState } from 'react';
import './QueueFilters.scss';

import { ModerationQueueFilter } from 'pages/Moderation/ModerationQueue/model/types';
import { DebounceInput } from 'react-debounce-input';
import { GetFlagsResponse } from 'redux/reducers/moderation/types';
import { Select } from 'ui/NewSelect/Select';

type ContentType = 'video' | 'image' | 'document';

const CONTENT_OPTIONS = [
  { id: 'video', label: 'video' },
  { id: 'image', label: 'image' },
  { id: 'document', label: 'document' },
] as const;

type Props = {
  value: ModerationQueueFilter;
  onChange: (v: ModerationQueueFilter) => void;
  flags: GetFlagsResponse | null;
};

export const QueueFilters: React.FC<Props> = ({ value, onChange, flags }) => {
  const [local, setLocal] = useState<ModerationQueueFilter>(value);
  const [search, setSearch] = useState(value.search ?? '');

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setLocal(value);
    setSearch(value.search ?? '');
  }, [value]);

  useEffect(() => {
    const merged = { ...local, search: debouncedSearch || undefined };
    setLocal(merged);
    onChange(merged);
  }, [debouncedSearch]);

  const update = (next: Partial<ModerationQueueFilter>) => {
    const merged: ModerationQueueFilter = {
      ...local,
      ...next,
      search: debouncedSearch || undefined,
    };
    setLocal(merged);
    onChange(merged);
  };

  const reset = () => {
    setSearch('');
    setLocal({});
    onChange({});
  };

  const isEmpty = useMemo(() => {
    return !local.contentType && !local.flag && !local.search && !local.date_from && !local.date_to;
  }, [local]);

  return (
    <div className="queue-filters">
      <button type="button" className="queue-filters__reset" disabled={isEmpty} onClick={reset}>
        Reset
      </button>

      <Select
        options={(flags?.data ?? []).map((f) => ({
          id: String(f.value), // ✅ string
          label: f.description,
        }))}
        value={local.flag != null ? String(local.flag) : undefined} // ✅ string | undefined
        placeholder="Complaint Type"
        onChange={(v: string) => update({ flag: v ? Number(v) : undefined })} // ✅ назад в number
        className="queue-filters__field"
      />

      <Select
        options={CONTENT_OPTIONS as any}
        value={local.contentType}
        placeholder="Content Type"
        onChange={(v: ContentType) => update({ contentType: v })}
        className="queue-filters__field"
      />

      <div className="queue-filters__search">
        <DebounceInput
          className="queue-filters__search-input"
          placeholder="Search"
          debounceTimeout={1000}
          value={search || ''}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className="queue-filters__search-clear"
            onClick={() => setSearch('')}
          >
            ×
          </button>
        )}
      </div>

      <div className="queue-filters__dates">
        <input
          type="date"
          className="queue-filters__date-input"
          value={local.date_from ?? ''}
          onChange={(e) => {
            const v = e.target.value || undefined;
            const to = local.date_to && v && v > local.date_to ? v : local.date_to;
            update({ date_from: v, date_to: to });
          }}
        />
        <span className="queue-filters__date-sep">—</span>
        <input
          type="date"
          className="queue-filters__date-input"
          value={local.date_to ?? ''}
          onChange={(e) => {
            const v = e.target.value || undefined;
            const from = local.date_from && v && v < local.date_from ? v : local.date_from;
            update({ date_to: v, date_from: from });
          }}
        />
      </div>
    </div>
  );
};

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
